import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MousePointer2, Users, UserCheck, Activity,
  AlertCircle, Loader2, ExternalLink, Globe,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import adminClient from '@/lib/api/adminClient';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from 'recharts';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

type Period = '7d' | '30d' | '90d';

interface GA4Overview {
  configured: boolean;
  sessions: number;
  new_users: number;
  active_users: number;
  engagement_rate: number;
}

interface TrafficSourceItem { channel: string; sessions: number; percentage: number; }
interface EventItem { event_name: string; count: number; }
interface PageItem { page_path: string; sessions: number; bounce_rate: number; }

interface GA4TrafficSources { configured: boolean; data: TrafficSourceItem[]; }
interface GA4Events { configured: boolean; data: EventItem[]; }
interface GA4TopPages { configured: boolean; data: PageItem[]; }

interface ConversionSourceItem {
  source_medium: string;
  signup_complete: number;
  trial_start: number;
  purchase: number;
  total: number;
}
interface GA4ConversionsBySource { configured: boolean; data: ConversionSourceItem[]; }

interface DailyTrendItem {
  date: string;
  sessions: number;
  new_users: number;
  key_events: number;
}
interface GA4DailyTrend { configured: boolean; data: DailyTrendItem[]; }

interface GeoItem {
  country: string;
  sessions: number;
  key_events: number;
  conversion_rate: number;
}
interface GA4Geo { configured: boolean; data: GeoItem[]; }

// ═══════════════════════════════════════════════════════════════
// Channel colors
// ═══════════════════════════════════════════════════════════════

const CHANNEL_COLORS: Record<string, string> = {
  'Organic Search': '#00d084',
  'Direct': '#3b82f6',
  'Social': '#f59e0b',
  'Referral': '#8b5cf6',
  'Paid Search': '#ef4444',
  'Email': '#06b6d4',
  'Organic Social': '#f97316',
  'Unassigned': '#9ca3af',
};

const channelColor = (channel: string) =>
  CHANNEL_COLORS[channel] ?? '#6b7280';

// Source/medium color helper
const SOURCE_COLORS: Record<string, string> = {
  'google / cpc': '#ef4444',
  'facebook / cpc': '#3b82f6',
  'facebook / paid': '#3b82f6',
  'instagram / paid': '#e879f9',
  'google / organic': '#00d084',
  '(direct) / (none)': '#9ca3af',
};
const sourceColor = (sm: string) =>
  SOURCE_COLORS[sm.toLowerCase()] ?? '#8b5cf6';

// Key conversion events to highlight
const KEY_EVENTS = new Set(['signup_complete', 'trial_start', 'purchase']);

// ═══════════════════════════════════════════════════════════════
// Setup guide (shown when not configured)
// ═══════════════════════════════════════════════════════════════

function SetupGuide() {
  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          <CardTitle className="text-base text-amber-600 dark:text-amber-400">
            GA4 尚未設定
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          請在後端 <code className="text-xs bg-muted px-1 py-0.5 rounded">.env</code> 設定以下環境變數，並重啟 backend 容器：
        </p>

        <div className="bg-muted rounded-lg p-4 space-y-2 font-mono text-xs overflow-x-auto">
          <div>
            <span className="text-green-500"># GA4 Property ID（純數字）</span>
            <br />
            <span className="text-brand-primary">GA4_PROPERTY_ID</span>=13593518254
          </div>
          <div className="mt-2">
            <span className="text-green-500"># Service Account JSON 金鑰（單行，完整 JSON 字串）</span>
            <br />
            <span className="text-brand-primary">GA4_SERVICE_ACCOUNT_JSON</span>={"{"}"type":"service_account","project_id":"sentinel-19fc7",...{"}"}
          </div>
          <div className="mt-2">
            <span className="text-green-500"># 或使用金鑰檔案路徑</span>
            <br />
            <span className="text-brand-primary">GOOGLE_APPLICATION_CREDENTIALS</span>=/path/to/sentinel-19fc7-keyfile.json
          </div>
        </div>

        <div className="space-y-1 text-muted-foreground">
          <p className="font-medium text-foreground">設定步驟：</p>
          <ol className="list-decimal list-inside space-y-1 ml-1">
            <li>
              GA4 Admin → 帳戶存取管理 → 新增使用者：
              <br />
              <code className="text-xs bg-muted px-1 py-0.5 rounded ml-4">
                sentinel-19fc7@appspot.gserviceaccount.com
              </code>
              &nbsp;給予「檢視者」權限
            </li>
            <li>將 JSON 金鑰內容（或路徑）填入 .env</li>
            <li>重啟 backend：<code className="text-xs bg-muted px-1 py-0.5 rounded">docker restart sentinel-backend</code></li>
          </ol>
        </div>

        <a
          href="https://console.cloud.google.com/iam-admin/serviceaccounts"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline"
        >
          Google Cloud 服務帳號管理 <ExternalLink className="w-3 h-3" />
        </a>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
// Stat card
// ═══════════════════════════════════════════════════════════════

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  loading?: boolean;
}

function StatCard({ label, value, icon: Icon, color, loading }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            {loading ? (
              <div className="h-8 w-20 bg-muted rounded animate-pulse mt-1" />
            ) : (
              <p className="text-2xl font-bold mt-1">{value}</p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
// Loading placeholder
// ═══════════════════════════════════════════════════════════════

function ChartPlaceholder({ notConfigured }: { notConfigured: boolean }) {
  return (
    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
      {notConfigured ? '—' : <Loader2 className="w-5 h-5 animate-spin" />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════

export function AnalyticsPage() {
  const [period, setPeriod] = useState<Period>('30d');

  const periodLabel: Record<Period, string> = { '7d': '近 7 天', '30d': '近 30 天', '90d': '近 90 天' };

  const queryOpts = { staleTime: 5 * 60 * 1000, retry: 1 };

  // Existing queries
  const { data: overview, isLoading: overviewLoading } = useQuery<GA4Overview>({
    queryKey: ['admin', 'ga4', 'overview', period],
    queryFn: () => adminClient.get(`/admin/ga4/overview?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  const { data: sources } = useQuery<GA4TrafficSources>({
    queryKey: ['admin', 'ga4', 'traffic-sources', period],
    queryFn: () => adminClient.get(`/admin/ga4/traffic-sources?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  const { data: events } = useQuery<GA4Events>({
    queryKey: ['admin', 'ga4', 'events', period],
    queryFn: () => adminClient.get(`/admin/ga4/events?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  const { data: pages } = useQuery<GA4TopPages>({
    queryKey: ['admin', 'ga4', 'top-pages', period],
    queryFn: () => adminClient.get(`/admin/ga4/top-pages?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  // New queries
  const { data: conversionsBySource } = useQuery<GA4ConversionsBySource>({
    queryKey: ['admin', 'ga4', 'conversions-by-source', period],
    queryFn: () => adminClient.get(`/admin/ga4/conversions-by-source?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  const { data: dailyTrend } = useQuery<GA4DailyTrend>({
    queryKey: ['admin', 'ga4', 'daily-trend', period],
    queryFn: () => adminClient.get(`/admin/ga4/daily-trend?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  const { data: geo } = useQuery<GA4Geo>({
    queryKey: ['admin', 'ga4', 'geo', period],
    queryFn: () => adminClient.get(`/admin/ga4/geo?period=${period}`).then(r => r.data),
    ...queryOpts,
  });

  const notConfigured = overview && !overview.configured;

  // Key events for conversion chart
  const keyEventData = (events?.data ?? []).filter(e => KEY_EVENTS.has(e.event_name));

  // Format daily trend dates for display
  const trendData = (dailyTrend?.data ?? []).map(d => ({
    ...d,
    label: d.date.slice(5), // "03-18"
  }));

  const tooltipStyle = { backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold">GA4 流量分析</h1>
        {/* Period selector */}
        <div className="flex gap-1 bg-muted rounded-lg p-1 self-start sm:self-auto">
          {(['7d', '30d', '90d'] as Period[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {periodLabel[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Setup guide — shown only when credentials not configured */}
      {notConfigured && <SetupGuide />}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="工作階段"
          value={overview?.sessions?.toLocaleString() ?? '—'}
          icon={MousePointer2}
          color="text-blue-500"
          loading={overviewLoading}
        />
        <StatCard
          label="新用戶"
          value={overview?.new_users?.toLocaleString() ?? '—'}
          icon={Users}
          color="text-green-500"
          loading={overviewLoading}
        />
        <StatCard
          label="活躍用戶"
          value={overview?.active_users?.toLocaleString() ?? '—'}
          icon={UserCheck}
          color="text-amber-500"
          loading={overviewLoading}
        />
        <StatCard
          label="參與率"
          value={overview ? `${overview.engagement_rate}%` : '—'}
          icon={Activity}
          color="text-purple-500"
          loading={overviewLoading}
        />
      </div>

      {/* Daily Trend Chart (NEW) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">每日趨勢</CardTitle>
        </CardHeader>
        <CardContent>
          {!trendData.length ? (
            <ChartPlaceholder notConfigured={!!notConfigured} />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                  yAxisId="left"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                  yAxisId="right"
                  orientation="right"
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="sessions"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  name="工作階段"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="new_users"
                  stroke="#00d084"
                  strokeWidth={2}
                  dot={false}
                  name="新用戶"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="key_events"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="重要活動"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Conversions by Source (NEW — most important) */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">轉換歸因（按廣告管道）</CardTitle>
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 font-medium">
              重要
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            每個來源/媒介的轉換次數 — 一眼看出 Google Ads vs Meta vs Organic 的效果
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!conversionsBySource?.data?.length ? (
            <div className="p-6">
              <ChartPlaceholder notConfigured={!!notConfigured} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2.5 text-muted-foreground font-medium">來源 / 媒介</th>
                    <th className="text-right px-4 py-2.5 text-muted-foreground font-medium">註冊</th>
                    <th className="text-right px-4 py-2.5 text-muted-foreground font-medium">試用</th>
                    <th className="text-right px-4 py-2.5 text-muted-foreground font-medium hidden sm:table-cell">購買</th>
                    <th className="text-right px-4 py-2.5 text-muted-foreground font-medium">合計</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionsBySource.data.map((row) => (
                    <tr key={row.source_medium} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: sourceColor(row.source_medium) }}
                          />
                          <span className="font-mono text-xs">{row.source_medium}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium">
                        {row.signup_complete || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium">
                        {row.trial_start || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium hidden sm:table-cell">
                        {row.purchase || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="font-bold text-brand-primary">{row.total}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Traffic Sources */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">流量來源</CardTitle>
          </CardHeader>
          <CardContent>
            {!sources?.data?.length ? (
              <ChartPlaceholder notConfigured={!!notConfigured} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={sources.data}
                  layout="vertical"
                  margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis
                    dataKey="channel"
                    type="category"
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                    width={110}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number, _: string, entry: any) => [
                      `${value.toLocaleString()} sessions (${entry.payload.percentage}%)`,
                      entry.payload.channel,
                    ]}
                  />
                  <Bar dataKey="sessions" radius={[0, 4, 4, 0]}>
                    {sources.data.map((entry) => (
                      <Cell key={entry.channel} fill={channelColor(entry.channel)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Conversion Events */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">轉換事件</CardTitle>
          </CardHeader>
          <CardContent>
            {!keyEventData.length ? (
              <ChartPlaceholder notConfigured={!!notConfigured} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={keyEventData} margin={{ left: 0, right: 16, top: 4, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="event_name"
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                    angle={-15}
                    textAnchor="end"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#00d084" radius={[4, 4, 0, 0]} name="次數" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Geographic Distribution (NEW) */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">地理分布（前 15 國）</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!geo?.data?.length ? (
            <div className="p-6">
              <ChartPlaceholder notConfigured={!!notConfigured} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium w-8">#</th>
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium">國家</th>
                    <th className="text-right px-4 py-2 text-muted-foreground font-medium">工作階段</th>
                    <th className="text-right px-4 py-2 text-muted-foreground font-medium">重要活動</th>
                    <th className="text-right px-4 py-2 text-muted-foreground font-medium hidden sm:table-cell">轉換率</th>
                  </tr>
                </thead>
                <tbody>
                  {geo.data.map((row, idx) => (
                    <tr key={row.country} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2 text-muted-foreground">{idx + 1}</td>
                      <td className="px-4 py-2 font-medium">{row.country}</td>
                      <td className="px-4 py-2 text-right">{row.sessions.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        {row.key_events ? (
                          <span className="font-medium text-brand-primary">{row.key_events}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right hidden sm:table-cell">
                        {row.conversion_rate > 0 ? (
                          <span className={row.conversion_rate >= 2 ? 'text-green-500 font-medium' : ''}>
                            {row.conversion_rate}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Events (compact list) */}
      {events?.data && events.data.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">所有事件（前 20）</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium">事件名稱</th>
                    <th className="text-right px-4 py-2 text-muted-foreground font-medium">次數</th>
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium hidden sm:table-cell">類型</th>
                  </tr>
                </thead>
                <tbody>
                  {events.data.map((ev) => (
                    <tr key={ev.event_name} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2 font-mono text-xs">{ev.event_name}</td>
                      <td className="px-4 py-2 text-right font-medium">{ev.count.toLocaleString()}</td>
                      <td className="px-4 py-2 hidden sm:table-cell">
                        {KEY_EVENTS.has(ev.event_name) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
                            轉換
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Pages */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">熱門頁面（前 10）</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!pages?.data?.length ? (
            <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
              {notConfigured ? '—' : <Loader2 className="w-5 h-5 animate-spin" />}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium w-8">#</th>
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium">頁面路徑</th>
                    <th className="text-right px-4 py-2 text-muted-foreground font-medium">工作階段</th>
                    <th className="text-right px-4 py-2 text-muted-foreground font-medium hidden sm:table-cell">
                      跳出率
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pages.data.map((page, idx) => (
                    <tr key={page.page_path} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2 text-muted-foreground">{idx + 1}</td>
                      <td className="px-4 py-2 font-mono text-xs max-w-[200px] sm:max-w-none truncate">
                        {page.page_path}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">{page.sessions.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground hidden sm:table-cell">
                        {page.bounce_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
